import { memo } from "react";
import styled from "@emotion/styled";
import { useField } from "formik";
import { TextField, Switch, Select, MenuItem, FormControlLabel, FormHelperText } from "@mui/material";

import { PatternFormat } from 'react-number-format';

import { InputLabel } from '../Common';

import ErrorMessage from "../MuiFields/ErrorMessage";

import DatePickerField from "./DatePicker";

import TimePickerField from "./TimePicker";

import RadioButtons from "./RadioButtons";

import ColorPicker from "./ColorPicker";

import ToggleButtonComponent from "./ToggleButtonComponent";
import CreditCardField from "./CreditCardField";

const SwitchContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  position: relative;
  padding-left: .6rem;

  .msg {
    position: absolute;
    top: 2.3rem;
    left: -.5rem;
    font-size: .8rem;
  }
`

/**
 * A custom form field component that uses Formik's useField hook to connect the component to the form.
 * It can be used to generate a form field with a label and a specific type (text, select, switch, radio, date, time, color).
 * The component will automatically show an error message if there is an error in the field.
 *
 * @param {object} props
 * @prop {string} label Optional. The label for the field.
 * @prop {string} size Optional. The size of the field. Can be "small" or "medium".
 * @prop {string} placeholder Optional. The placeholder text for the field.
 * @prop {boolean} showSuccess Optional. If true, the component will show a green checkmark if there is no error in the field.
 * @prop {object} InputProps Optional. Additional props to pass to the Input component.
 * @prop {string} color Optional. The color scheme for the component. Can be "primary", "secondary", or "success".
 * @prop {string} formFieldType Required.The type of field to generate. Can be "text", "select", "switch", "radio", "date", "time", or "color".
 * @prop {array} options Required for Select. An array of options for the select field.
 * @prop {array} values Required for Radio. An array of values for the radio buttons.
 * @prop {string} helperText Optional. Additional text to show below the field.
 * @prop {object} ...props Additional props to pass to the underlying component.
 */
const FormikField = ({label, size="small", placeholder, showSuccess = false, InputProps, color, formFieldType = "text", options=[], values = [], helperText, blur, maskFormat, maskChar = "_", changeHandler = null, ...props}) => {
  const [ field, meta, helpers ] = useField(props);

  const {  onChange, name, value } = field;

  let defaultColor = color || "primary";
  let currentColor = color;
  if (showSuccess) {
    // If showSuccess is true and there is no error, set color to success
    if (!meta.error && value) {
      currentColor = "success";
    }
  }

  switch (formFieldType) {
    case "select":
      return (
        <>
        <InputLabel>{props.required ? `${label} *` : label}</InputLabel>
        <Select fullWidth color={currentColor} size={size} error={meta.touched && meta.error ? true : false} slotProps={{input: InputProps}} {...field} {...props} >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </Select>
        {meta.touched && meta.error ? <ErrorMessage error={true} message={meta.error} /> : null}
        </>
      )
    case "switch":
      return (
        <SwitchContainer>
          <FormControlLabel
            sx={{ gap: "1rem" }}
            control={
              <Switch
                {...props}
                color={currentColor}
                size={size}
                slotProps={{ input: InputProps }}
                name={field.name}
                checked={!!field.value}
                onChange={(e) => helpers.setValue(e.target.checked)}
                onBlur={field.onBlur}
              />
            }
            label={label}
          />
          <div className="msg">
            {meta.touched && meta.error 
              ? <ErrorMessage error={true} message={meta.error} /> 
              : helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
          </div>
        </SwitchContainer>
      )
    case "radio":
      return (
        <>
        <InputLabel>{props.required ? `${label} *` : label}</InputLabel>
        <RadioButtons {...props} fields={values} />
        {meta.touched && meta.error ? <ErrorMessage error={true} message={meta.error} /> : null}
        </>
      )
    case "toggle":
      return (
        <>
        <InputLabel>{props.required ? `${label} *` : label}</InputLabel>
        <ToggleButtonComponent {...props} options={options} color={color || "secondary"} />
        {meta.touched && meta.error ? <ErrorMessage error={true} message={meta.error} /> : null}
        </>
      )
    case "date":
      return (
        <>
        <InputLabel>{props.required ? `${label} *` : label}</InputLabel>
        <DatePickerField {...field} {...props}  />
        {meta.error ? <ErrorMessage error={true} message={meta.error} /> : null}
        </>
      )
      case "time":
        return (
          <>
          <InputLabel>{props.required ? `${label} *` : label}</InputLabel>
          <TimePickerField {...field} {...props}  />
          {meta.error ? <ErrorMessage error={true} message={meta.error} /> : null}
          </>
        )
      case "color":
        return (
          <>
          <InputLabel>{props.required ? `${label} *` : label}</InputLabel>
          <ColorPicker {...props}  />
          {meta.touched && meta.error ? <ErrorMessage error={true} message={meta.error} /> : helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
          </>
        )
    case "card":
      return (
        <>
        {label ? <InputLabel>{props.required ? `${label} *` : label}</InputLabel> : null}
        <CreditCardField {...props} placeholder={placeholder} label={label} size={size} />
        </>
      );
    case "mask":
      return (
        <>
          {label ? <InputLabel>{props.required ? `${label} *` : label}</InputLabel> : null}
          <PatternFormat
            {...props}
            {...field}
            placeholder={placeholder}
            format={maskFormat}
            mask={maskChar}
            customInput={TextField} // Tells the library to use MUI's TextField
            fullWidth
            size={size}
            error={meta.touched && meta.error}
            onChange={() => {}}
            onValueChange={(values) => {
              helpers.setValue(values.value); // Sends raw value to Formik
            }}
            // Standard MUI props passed through
            slotProps={{
              input: {
                ...InputProps,
                onBlur: (e) => {
                  if (blur) blur(e);
                  field.onBlur(e);
                }
              }
            }}
          />
          {meta.touched && meta.error ? <ErrorMessage error={true} message={meta.error} /> : null}
        </>
      );
    default:
      return (
        <>
        {label ? <InputLabel>{props.required ? `${label} *` : label}</InputLabel> : null}
        <TextField placeholder={placeholder} fullWidth {...props}  color={currentColor} size={size} error={meta.touched && meta.error ? true : false} slotProps={{input: {...InputProps, onBlur: (e) => {
          if (blur) {
            blur(e);
          }
          field.onBlur(e);
        }}}} {...field} onChange={(e) => {
          if (changeHandler) {
            changeHandler(e)
          }
          onChange(e);
        }} />
        {meta.touched && meta.error ? <ErrorMessage error={true} message={meta.error} /> : helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
        </>
      )
    }
}

export default FormikField;